/**
 * Market Hours & Status Utilities
 */
export type MarketStatus = 'Open' | 'Pre market' | 'After market' | 'Day market' | 'Closed';

export function getMarketStatus(market: 'US' | 'KR' | 'ALL' = 'US'): MarketStatus {
  if (market === 'KR') return getKRMarketStatus();
  return getUSMarketStatus();
}

export function getUSMarketStatus(): MarketStatus {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric'
  });

  const parts = formatter.formatToParts(now);
  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  const dayName = getPart('weekday');
  const hour = parseInt(getPart('hour'), 10);
  const minute = parseInt(getPart('minute'), 10);
  const timeInMinutes = hour * 60 + minute;

  if (dayName === 'Sat' || dayName === 'Sun') return 'Closed';

  if (timeInMinutes >= 9 * 60 + 30 && timeInMinutes < 16 * 60) return 'Day market';
  if (timeInMinutes >= 4 * 60 && timeInMinutes < 9 * 60 + 30) return 'Pre market';
  if (timeInMinutes >= 16 * 60 && timeInMinutes < 20 * 60) return 'After market';

  return 'Closed';
}

export function getKRMarketStatus(): MarketStatus {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    hour12: false,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric'
  });

  const parts = formatter.formatToParts(now);
  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  const dayName = getPart('weekday');
  const hour = parseInt(getPart('hour'), 10);
  const minute = parseInt(getPart('minute'), 10);
  const timeInMinutes = hour * 60 + minute;

  if (dayName === 'Sat' || dayName === 'Sun') return 'Closed';

  // KR Regular: 09:00 - 15:30
  if (timeInMinutes >= 9 * 60 && timeInMinutes < 15 * 60 + 30) return 'Day market';
  // KR Pre: 08:30 - 09:00
  if (timeInMinutes >= 8 * 60 + 30 && timeInMinutes < 9 * 60) return 'Pre market';
  // KR After: 15:30 - 18:00
  if (timeInMinutes >= 15 * 60 + 30 && timeInMinutes < 18 * 60) return 'After market';

  return 'Closed';
}

export function getMarketStatusColor(status: MarketStatus): string {
  switch (status) {
    case 'Day market': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    case 'Pre market': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    case 'After market': return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
    default: return 'text-white/40 bg-white/5 border-white/10';
  }
}

export function getCurrencySymbol(market: 'US' | 'KR' | 'ALL' = 'US'): string {
  return market === 'KR' ? '₩' : '$';
}

export function formatCurrency(amount: number, market: 'US' | 'KR' | 'ALL' = 'US', compact: boolean = false): string {
  const actualMarket = market === 'ALL' ? 'US' : market;
  const symbol = getCurrencySymbol(actualMarket);
  
  if (actualMarket === 'KR') {
    // KRW usually doesn't show decimals for regular prices
    if (compact && amount >= 1000000) {
      return `${symbol}${(amount / 1000000).toFixed(1)}M`;
    }
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
  
  // USD
  return `${symbol}${amount.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
}

export function formatBps(value: number): string {
  // If the value is small (e.g. 0.01 meaning 1%), we might need to handle scaling.
  // But usually indicators are provided in percentage points (e.g. 5.25 for 5.25%).
  // According to current usage, they are displayed as value.toFixed(2) + '%'.
  // So 0.65 means 0.65%. 0.65% = 65 bp.
  // Using 1 decimal place for bp provides better precision for yield spreads.
  const bps = value * 100;
  return `${bps.toFixed(bps % 1 === 0 ? 0 : 1)} bp`;
}
